/**
 * Performance Stats Page Configuration
 * Defines the columns and settings specific to the Performance Stats view (Publishers/Demand)
 */

import { ColumnIds } from "./types";
import { datagridColumns } from "./datagridCommon";

/**
 * Load performance stats-specific column configurations into the datagrid
 * This function should be called when initializing the Performance Stats page
 *
 * Sets column visibility to match the old Python dashboard's "Supply Daily Stats" view
 */
export const loadPerformancePageColumns = () => {
  // First, hide all metric columns by default
  const allColumns = Array.from(datagridColumns.values());
  allColumns.forEach(col => {
    // Only hide non-groupBy columns (metrics)
    if (!col.isGroupByColumn) {
      col.visible = false;
    }
  });

  // Then show only the columns that should be visible in performance view
  // These match the old Python dashboard's "Supply Daily Stats" layout
  const visibleColumns = [
    ColumnIds.revenueApptap,           // Gross Revenue
    // Revenue Change is calculated client-side, not a column
    ColumnIds.revenuePublisher,        // Publisher Payout
    ColumnIds.reportedClickCost,       // Reported Cost
    ColumnIds.apptapNetRevenue,        // Approx Net Revenue
    ColumnIds.clicksReceived,          // Clicks
    ColumnIds.clicksTracked,           // Tracked Clicks
    ColumnIds.publisherTrackedCreditRate, // Tracked %
    ColumnIds.conversionsReceived,     // Conversions
    ColumnIds.conversionsCredited,     // Credited Conversions
    ColumnIds.trackedCreditRate,       // Credited %
    // Average CR - need to check what this maps to
    ColumnIds.avgCPA,                  // Effective CPA
  ];

  visibleColumns.forEach(colId => {
    const col = datagridColumns.get(colId);
    if (col) {
      col.visible = true;
    }
  });
};

/**
 * Get the default column visibility for performance view
 * Returns an array of column IDs that should be visible by default
 */
export const getPerformanceDefaultVisibleColumns = (): ColumnIds[] => {
  return [
    ColumnIds.apptapPublisherName,
    ColumnIds.apptapPublisherID,
    ColumnIds.apptapSiteID,
    ColumnIds.date,
    ColumnIds.revenueApptap,
    ColumnIds.revenuePublisher,
    ColumnIds.reportedClickCost,
    ColumnIds.apptapNetRevenue,
    ColumnIds.clicksReceived,
    ColumnIds.clicksTracked,
    ColumnIds.conversionsReceived,
    ColumnIds.conversionsCredited,
    ColumnIds.trackedCreditRate,
    ColumnIds.avgCPA,
    ColumnIds.eCPC,
  ];
};

/**
 * Get the fields list configuration for performance view
 * Matches the Python dashboard's get_publisher_fields_list function
 */
export const getPerformanceFieldsList = (periodType: "daily" | "monthly", detail: boolean = false) => {
  const fields: Array<{id: ColumnIds, format: string, label: string, tooltip?: string}> = [
    { id: ColumnIds.revenueApptap, format: "currency", label: "Gross Revenue" },
    // Note: "RevenueChange" is calculated client-side based on prior period
    { id: ColumnIds.revenuePublisher, format: "currency", label: "Publisher Payout" },
    { id: ColumnIds.reportedClickCost, format: "currency", label: "Reported Cost" },
    { id: ColumnIds.apptapNetRevenue, format: "currency", label: "Approx Net Revenue" },
  ];

  if (detail) {
    // Add separator and impressions (when available)
    // fields.push({ id: ColumnIds.impressions, format: "number", label: "Impressions" });
  }

  // Add click metrics
  fields.push(
    { id: ColumnIds.clicksReceived, format: "number", label: "Clicks" },
    { id: ColumnIds.clicksTracked, format: "number", label: "Tracked Clicks" },
    { id: ColumnIds.publisherTrackedCreditRate, format: "percent", label: "Tracked %", tooltip: "Percentage of clicks that were tracked" }
  );

  // Add conversion metrics
  fields.push(
    { id: ColumnIds.conversionsReceived, format: "number", label: "Conversions" },
    { id: ColumnIds.conversionsCredited, format: "number", label: "Credited Conversions" },
    { id: ColumnIds.trackedCreditRate, format: "percent", label: "Credited %", tooltip: "Percentage of conversions that were credited" }
  );

  // Add calculated metrics
  fields.push(
    { id: ColumnIds.trackedCreditRate, format: "percent", label: "Average CR", tooltip: "Conversion Rate" },
    { id: ColumnIds.avgCPA, format: "currency3", label: "Effective CPA", tooltip: "Average Revenue Per Conversion" }
  );

  if (detail) {
    fields.push(
      { id: ColumnIds.eCPC, format: "currency3", label: "Effective CPC", tooltip: "Average Revenue Per Click" }
    );
  }

  return fields;
};

/**
 * Determine if statistical highlighting should be enabled
 * for the current view configuration
 */
export const shouldEnableStatisticalHighlighting = (
  page: string,
  groupBy: string | null,
  pivotBy: string | null
): boolean => {
  // Enable for performanceStats page when grouping by time periods
  if (page !== "performanceStats") return false;

  if (!groupBy) return false;

  // Check if grouping by date, week, or month
  const hasTimeDimension = groupBy.includes("date") ||
                           groupBy.includes("week") ||
                           groupBy.includes("month");

  return hasTimeDimension;
};

/**
 * Get the date field being used for grouping
 */
export const getDateFieldFromGroupBy = (groupBy: string | null): "date" | "week" | "month" | null => {
  if (!groupBy) return null;

  if (groupBy.includes("date")) return "date";
  if (groupBy.includes("week")) return "week";
  if (groupBy.includes("month")) return "month";

  return null;
};
